
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    IconButton,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Tooltip,
    Avatar,
    Chip,
} from '@mui/material';
import {
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    Add as AddIcon,
    Event as EventIcon,
    LocationOn as LocationIcon,
    AccessTime as TimeIcon,
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, addMonths, subMonths } from 'date-fns';
import client from '../../api/client';

const CalendarPage = () => {
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [eventData, setEventData] = useState({
        title: '',
        description: '',
        start_time: '',
        end_time: '',
        location: '',
        type: 'meeting'
    });

    const fetchEvents = async () => {
        try {
            const res = await client.get('/calendar/events');
            if (res.data.success) {
                setEvents(res.data.data);
            }
        } catch (err) {
            console.error('Error fetching events:', err);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    const renderHeader = () => (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{format(currentMonth, 'MMMM yyyy')}</Typography>
                <Box>
                    <IconButton size="small" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}><ChevronLeftIcon /></IconButton>
                    <IconButton size="small" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}><ChevronRightIcon /></IconButton>
                </Box>
                <Button variant="outlined" size="small" onClick={() => setCurrentMonth(new Date())}>Today</Button>
            </Box>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                    setEventData({ ...eventData, start_time: format(new Date(), "yyyy-MM-dd'T'HH:mm"), end_time: format(new Date(), "yyyy-MM-dd'T'HH:mm") });
                    setOpenDialog(true);
                }}
                sx={{ borderRadius: '20px', textTransform: 'none' }}
            >
                New Event
            </Button>
        </Box>
    );

    const renderDays = () => {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return (
            <Grid container spacing={0} sx={{ borderBottom: '1px solid #e0e0e0', mb: 1 }}>
                {days.map(day => (
                    <Grid item xs={12 / 7} key={day} sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>{day.toUpperCase()}</Typography>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderCells = () => {
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(monthStart);
        const startDate = startOfWeek(monthStart);
        const endDate = endOfWeek(monthEnd);

        const rows = [];
        let days = [];
        let day = startDate;
        let formattedDate = "";

        while (day <= endDate) {
            for (let i = 0; i < 7; i++) {
                formattedDate = format(day, "d");
                const cloneDay = day;
                const dayEvents = events.filter(e => isSameDay(new Date(e.start_time), cloneDay));

                days.push(
                    <Grid
                        item
                        xs={12 / 7}
                        key={day}
                        sx={{
                            height: 120,
                            border: '1px solid #f0f0f0',
                            bgcolor: !isSameMonth(day, monthStart) ? '#fcfcfc' : 'white',
                            p: 0.5,
                            transition: '0.2s',
                            '&:hover': { bgcolor: '#f5f5f5' }
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: isSameDay(day, new Date()) ? 700 : 400,
                                    color: isSameDay(day, new Date()) ? 'primary.main' : 'text.primary',
                                    bgcolor: isSameDay(day, new Date()) ? '#e3f2fd' : 'transparent',
                                    width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%'
                                }}
                            >
                                {formattedDate}
                            </Typography>
                        </Box>
                        <Box sx={{ overflowY: 'auto', maxHeight: 85 }}>
                            {dayEvents.map(event => (
                                <Tooltip title={`${event.title} (${format(new Date(event.start_time), 'HH:mm')})`} key={event.id}>
                                    <Box
                                        sx={{
                                            bgcolor: event.type === 'meeting' ? '#e3f2fd' : event.type === 'task' ? '#f1f8e9' : '#fff3e0',
                                            color: event.type === 'meeting' ? '#1976d2' : event.type === 'task' ? '#2e7d32' : '#e65100',
                                            fontSize: '0.7rem',
                                            p: 0.5,
                                            mb: 0.5,
                                            borderRadius: '4px',
                                            borderLeft: '3px solid',
                                            borderColor: 'inherit',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {event.title}
                                    </Box>
                                </Tooltip>
                            ))}
                        </Box>
                    </Grid>
                );
                day = addDays(day, 1);
            }
            rows.push(<Grid container spacing={0} key={day}>{days}</Grid>);
            days = [];
        }
        return <Box>{rows}</Box>;
    };

    const handleSaveEvent = async () => {
        try {
            const res = await client.post('/calendar/events', eventData);
            if (res.data.success) {
                setOpenDialog(false);
                fetchEvents();
                setEventData({ title: '', description: '', start_time: '', end_time: '', location: '', type: 'meeting' });
            }
        } catch (err) {
            console.error('Error saving event:', err);
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 80px)' }}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: '12px', border: '1px solid #e0e0e0' }}>
                {renderHeader()}
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                    {renderDays()}
                    {renderCells()}
                </Box>
            </Paper>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Create New Event</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Event Title"
                                value={eventData.title}
                                onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Start Time"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={eventData.start_time}
                                onChange={(e) => setEventData({ ...eventData, start_time: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="End Time"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={eventData.end_time}
                                onChange={(e) => setEventData({ ...eventData, end_time: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Location"
                                value={eventData.location}
                                onChange={(e) => setEventData({ ...eventData, location: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                select
                                label="Category"
                                value={eventData.type}
                                onChange={(e) => setEventData({ ...eventData, type: e.target.value })}
                            >
                                <MenuItem value="meeting">Meeting</MenuItem>
                                <MenuItem value="task">Task</MenuItem>
                                <MenuItem value="personal">Personal</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Description"
                                value={eventData.description}
                                onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveEvent} sx={{ borderRadius: '20px', px: 4 }}>Save Event</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CalendarPage;
